import { Injectable, Inject } from '@angular/core';
import { ApiService } from 'src/app/core/services/api.service';

import { LanguageEvents } from './events';
import { ENV_CONFIG } from 'src/app/core/injection.token';

@Injectable()
export class DictionaryAPIService {
  public constructor(
    @Inject(ApiService) private _apiService: ApiService,
    @Inject(ENV_CONFIG) private _envConfig: env.EnvData
  ) {}

  private readonly controller = 'dictionary';

  public getDictionaryAssetDocumentRequestTypes() {
    const request = 'getDictionaryAssetDocumentRequestTypes';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);

    return this._apiService.get<app.models.DictionaryRequestTypeModel[]>(url);
  }

  public getDictionaryPartnerType() {
    const request = 'getDictionaryPartnerType';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);

    return this._apiService.get<app.models.DictionaryDataModel[]>(url);
  }

  public getDictionaries() {
    const request = 'getDictionaries';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);

    return this._apiService.get<app.models.DictionaryDataModel[]>(url);
  }

  public getDictionaryTranslation(dictionaryId: string, languageCode: string) {
    const request = 'getDictionaryTranslation';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);
    const data = { dictionaryId: dictionaryId, languageCode: languageCode };

    return this._apiService.get<app.models.DictionaryTranslation>(url, data);
  }

  public saveDictionaryTranslation(dictionaryTranslation: any) {
    const request = 'saveDictionaryTranslation';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);

    const data = {
      dictionaryId: dictionaryTranslation.dictionaryId,
      data: dictionaryTranslation.data,
      language: dictionaryTranslation.language,
    };

    return this._apiService.postAsync<app.models.ServerResponseModel>(url, data, [
      LanguageEvents.DictionaryTranslationSaved,
    ]);
  }

  public getDictionaryContractModificationRequestType() {
    const request = 'getDictionaryContractModificationRequestType';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);

    return this._apiService.get<app.models.DictionaryRequestTypeModel[]>(url);
  }

  public getDictionaryReimbursementType() {
    const request = 'getDictionaryReimbursementType';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);

    return this._apiService.get<app.models.DictionaryDataModel[]>(url);
  }

  public getDictionaryPaymentSource(isActive: boolean = null) {
    const request = 'getDictionaryPaymentSource';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request, {
      IsActive: isActive,
    });

    return this._apiService.get<app.models.DictionaryDataModel[]>(url);
  }

  public getDictionaryContractModificationRequestReason(
    requestTypeId: string,
    reimbursementTypeId: string,
    isActive: boolean = null
  ) {
    const request = 'getDictionaryContractModificationRequestReason';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request, {
      DictionaryContractModificationRequestTypeId: requestTypeId,
      DictionaryReimbursementTypeId: reimbursementTypeId,
      IsActive: isActive,
    });

    return this._apiService.get<app.models.DictionaryContractModificationRequestReason[]>(url);
  }

  public getDictionaryContractModificationRequestPurpose(requestTypeId: string) {
    const request = 'getDictionaryContractModificationRequestPurpose';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request, {
      DictionaryContractModificationRequestTypeId: requestTypeId,
    });

    return this._apiService.get<app.models.DictionaryContractModificationRequestPurpose[]>(url);
  }

  public getDictionaryRequestType() {
    const request = 'getDictionaryRequestType';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);

    return this._apiService.get<app.models.DictionaryRequestType[]>(url);
  }

  public getClientDataRequestTypes() {
    const request = 'getClientDataRequestTypes';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);

    return this._apiService.get<app.models.DynamicResponse<app.models.ClientDataRequestType[]>>(url);
  }

  public getDictionaryClientDataRequestAddressTypes() {
    const request = 'getDictionaryClientDataRequestAddressTypes';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);

    return this._apiService.get<app.models.DynamicResponse<app.models.DictionaryDataModel[]>>(url);
  }

  public getDictionaryClientDataRequestTypes() {
    const request = 'getDictionaryClientDataRequestTypes';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);

    return this._apiService.get<app.models.DictionaryRequestTypeModel[]>(url);
  }

  public getMaritalStatuses() {
    const request = 'getMaritalStatuses';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);

    return this._apiService.get<app.models.DynamicResponse<app.models.DictionaryDataModel[]>>(url);
  }

  public getDictionaryContractTypes() {
    const request = 'getDictionaryContractTypes';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);

    return this._apiService.get<app.models.DynamicResponse<app.models.DictionaryDataModel[]>>(url);
  }
  public getDictionaryRequestStatuses() {
    const request = 'getDictionaryRequestStatuses';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);

    return this._apiService.get<app.models.DynamicResponse<app.models.DictionaryDataModel[]>>(url);
  }

  public getDictionaryTwoFactorAuthenticationTypes() {
    const request = 'getDictionaryTwoFactorAuthenticationTypes';
    const url = this._apiService.buildUrl(this._envConfig.ISSUER + '/api', this.controller, request);

    return this._apiService.get<app.models.DictionaryDataModel[]>(url);
  }

  public getAssetDocumentRequestTypeParamsByCodes(parameterCodes: string[]) {
    const request = 'getAssetDocumentRequestTypeParamsByCodes';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);
    const body = { parameterCodes: parameterCodes };

    return this._apiService.postGet<Map<string, string>>(url, body);
  }

  public getDictionaryCampaignTypes() {
    const request = 'getDictionaryCampaignTypes';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);

    return this._apiService.get<app.models.DictionaryDataModel[]>(url);
  }

  public getDictionaryCurrencies() {
    const request = 'getDictionaryCurrencies';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);

    return this._apiService.get<app.models.DictionaryDataModel[]>(url);
  }

  public getDictionarySegmentTypes() {
    const request = 'getDictionarySegmentTypes';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);

    return this._apiService.get<app.models.DictionaryDataModel[]>(url);
  }

  public getDictionarySegmentItems(dictionarySegmentItemTypeId: string) {
    const request = 'getDictionarySegmentItems';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request, {
      dictionarySegmentItemTypeId: dictionarySegmentItemTypeId,
    });

    return this._apiService.get<app.models.DictionaryDataModel[]>(url);
  }
}
